// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).

import { useShowDeviceDelivery } from "../../app/UiConfigContext.jsx";
import { getFileSignedUrl } from "../../data/storage";
import { supplierMeta, useUniformSuppliers } from "../../hooks/useUniformSuppliers";
import { deriveSupplierType } from "../../services/approvals";
import { PANEL } from "../../utils/constants";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { isPrimary } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { calcChildTotal, money } from "../../utils/helpers";
import { printChild, UniformTable } from "../form/ChildSections.jsx";
import { ROField } from "./RequestDetailAtoms.jsx";
import { DetailSection } from "./RequestDetailPanels.jsx";

export function DraftChildDisplay({ req, isStaff, sectionsOpen, setSectionsOpen }) {
  const { suppliers: uniformSuppliers } = useUniformSuppliers();
  const showDeviceDelivery = useShowDeviceDelivery();
  return (req.children || []).map(function (child, ci) {
    var childFees = child.fees || [];
    var childUni = child.uniforms || null;
    var childShoes = child.shoes || null;
    var childLaptop = child.laptop || null;
    var hasAny = childFees.length > 0 || childUni || childShoes || childLaptop;
    if (!hasAny) return null;
    // Print-Authorisation / Order button gate: derive from the supplier ROW (db-50),
    // not a hardcoded name list. Shows for authority suppliers + School Locker.
    const uMeta = childUni ? supplierMeta(uniformSuppliers, childUni.supplier || "") : null;
    const uType = uMeta
      ? deriveSupplierType({
          kind: uMeta.kind,
          payment_method: uMeta.paymentMethod,
          document_type: uMeta.documentType,
        })
      : null;
    const childTotal = calcChildTotal(child).total;
    return (
      <DetailSection
        key={child._key || ci}
        title={
          "Child " +
          (ci + 1) +
          " — " +
          (child.gender || "") +
          (child.yearLevel ? ` · ${child.yearLevel}` : "") +
          (child.schoolName ? ` — ${child.schoolName}` : "")
        }
        colour={"#00695C"}
        forceOpen={sectionsOpen}
        onToggle={function () {
          setSectionsOpen(null);
        }}
        totalLine={childTotal > 0 ? `Total: ${money(childTotal)}` : null}
      >
        {childFees.length > 0 && (
          <div
            className="mb-3 py-3 px-[14px] rounded-[8px] border-l-4"
            style={{ background: PANEL.bg, borderLeftColor: PANEL.border }}
          >
            <div className="text-xs font-bold text-zephyr-panel-heading uppercase tracking-[0.4px] mb-2">
              School Costs &amp; Stationery
            </div>
            {childFees.map(function (f, fi) {
              return (
                <div
                  key={fi}
                  className="grid grid-cols-4 gap-2 mb-[6px] bg-[#F5F5F5] rounded-[6px] py-2 px-[10px]"
                >
                  <ROField label="Fee Type" value={f.feeType} />
                  <ROField label="Amount" value={money(f.feeAmount)} />
                  <ROField label="Description" value={f.feeDesc} span={2} />
                </div>
              );
            })}
          </div>
        )}
        {childUni && (
          <div
            className="mb-3 py-3 px-[14px] rounded-[8px] border-l-4"
            style={{ background: PANEL.bg, borderLeftColor: PANEL.border }}
          >
            <div className="text-xs font-bold text-zephyr-panel-heading uppercase tracking-[0.4px] mb-[6px] flex items-center justify-between">
              <span>
                {`Uniforms — ${childUni.supplier || "—"}`}
                {isStaff && childUni.isReimbursement && (
                  <span className="ml-2 text-[10px] bg-zephyr-gold-light text-zephyr-orange border border-zephyr-gold rounded-[4px] py-0.5 px-[6px]">
                    REIMB
                  </span>
                )}
              </span>
              {childUni.approvalStatus === APPROVAL_STATUSES.APPROVED &&
                (uType === "school_locker" || uType === "authority") && (
                  <button
                    onClick={function () {
                      var isSL = childUni.supplier === "School Locker";
                      printChild({
                        child: Object.assign({}, childUni, {
                          gender: child.gender,
                          yearLevel: child.yearLevel,
                          schoolName: child.schoolName,
                        }),
                        childIndex: ci,
                        shelterName: req.shelterName,
                        refNo: req.refNo,
                        isSL: isSL,
                        shelterCode: req.shelterCode || "",
                        shelterRef: req.shelterRef || "",
                        reqDate: req.date || "",
                        totalChildren:
                          (req.children || []).filter(function (ch) {
                            return (
                              ch.uniforms &&
                              ch.uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED
                            );
                          }).length || 1,
                      });
                    }}
                    className="border border-zephyr-panel-heading text-zephyr-panel-heading rounded-[6px] py-1 px-3 text-[11px] font-semibold cursor-pointer"
                    style={{ background: "none", fontFamily: "inherit" }}
                  >
                    {childUni.supplier === "School Locker"
                      ? `${GLYPH.printer} Print Order Sent`
                      : `${GLYPH.printer} Print Authorisation`}
                  </button>
                )}
            </div>
            {isStaff && childUni.authorisedPerson && (
              <ROField label="Authorised Person" value={childUni.authorisedPerson} />
            )}
            {isStaff && childUni.authorisedContact && (
              <ROField label="Authorised Contact" value={childUni.authorisedContact} />
            )}
            {childUni.supplier === "School Locker" && (childUni.mobile || childUni.email) && (
              <div className="flex gap-4 mb-2">
                {childUni.mobile && <ROField label="Mobile (SMS pickup)" value={childUni.mobile} />}
                {childUni.email && <ROField label="Email (notification)" value={childUni.email} />}
              </div>
            )}
            <UniformTable
              child={Object.assign({}, childUni, {
                gender: child.gender,
                yearLevel: child.yearLevel,
                schoolName: child.schoolName,
              })}
              onChange={function () {}}
              showSku={childUni.supplier === "School Locker"}
              isCold={childUni.isColdClimate}
              locked={true}
            />
          </div>
        )}
        {childShoes && (
          <div
            className="mb-3 py-3 px-[14px] rounded-[8px] border-l-4"
            style={{ background: PANEL.bg, borderLeftColor: PANEL.border }}
          >
            <div className="text-xs font-bold text-zephyr-panel-heading uppercase tracking-[0.4px] mb-[6px]">
              Shoes
            </div>
            {/* Two halves: the Joggers pair sits on the left, the Leather pair starts
                mid-panel (col 2 = 50%). Within each half the Qty + Cost boxes sit
                tight together (a flex row), not spread across the whole panel. */}
            <div className="grid gap-2" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <ROField
                  label={
                    <span>
                      <span style={{ fontSize: "1.2em" }}>Joggers</span>
                      <br />
                      Qty
                    </span>
                  }
                  value={String(childShoes.jQty || 0)}
                  boxStyle={{ maxWidth: 70 }}
                />
                <ROField
                  label="Cost"
                  value={childShoes.jCost ? money(childShoes.jCost) : "—"}
                  boxStyle={{ maxWidth: 95 }}
                />
              </div>
              {!isPrimary(child.yearLevel) && (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                  <ROField
                    label={
                      <span>
                        <span style={{ fontSize: "1.2em" }}>Leather</span>
                        <br />
                        Qty
                      </span>
                    }
                    value={String(childShoes.lQty || 0)}
                    boxStyle={{ maxWidth: 70 }}
                  />
                  <ROField
                    label="Cost"
                    value={childShoes.lCost ? money(childShoes.lCost) : "—"}
                    boxStyle={{ maxWidth: 95 }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {childLaptop && (
          <div
            className="mb-3 py-3 px-[14px] rounded-[8px] border-l-4"
            style={{ background: PANEL.bg, borderLeftColor: PANEL.border }}
          >
            <div className="text-xs font-bold text-zephyr-panel-heading uppercase tracking-[0.4px] mb-[6px]">
              Device
            </div>
            <div className="grid grid-cols-4 gap-2">
              <ROField label="Device Type" value={childLaptop.deviceType} />
              <ROField
                label="Est. Cost"
                value={childLaptop.estimatedCost ? money(childLaptop.estimatedCost) : "—"}
              />
              <ROField label="Why Needed" value={childLaptop.whyNeeded} span={4} />
              {showDeviceDelivery && childLaptop.deliveryAddress ? (
                <ROField label="Delivery Address" value={childLaptop.deliveryAddress} span={4} />
              ) : null}
              {child.yearLevel !== "TAFE" && (
                <ROField label="BYOD Policy" value={childLaptop.hasSchoolDevice ? "Yes" : "No"} />
              )}
              <ROField label="Internet Safety" value={childLaptop.internetSafety ? "Yes" : "No"} />
              <ROField label="Terms Accepted" value={childLaptop.termsAccepted ? "Yes" : "No"} />
              {childLaptop.byod && typeof childLaptop.byod === "string" ? (
                <div>
                  <div className="text-[11px] text-zephyr-muted font-bold uppercase tracking-[0.4px] mb-1">
                    {child.yearLevel === "TAFE" ? "Enrolment Doc" : "BYOD Doc"}
                  </div>
                  <button
                    type="button"
                    onClick={async function () {
                      // Private bucket (db-168): sign the stored PATH on demand.
                      const u = await getFileSignedUrl(childLaptop.byod);
                      if (u) window.open(u, "_blank", "noopener,noreferrer");
                    }}
                    className="inline-flex items-center gap-[6px] bg-zephyr-teal-light border border-[#00897B40] rounded-[7px] py-[7px] px-3 text-[13px] text-zephyr-teal-dark cursor-pointer font-semibold"
                  >
                    {"\u{1F4C4} " +
                      (child.yearLevel === "TAFE" ? "Enrolment Document" : "BYOD Document") +
                      ` ${GLYPH.arrowDown}`}
                  </button>
                </div>
              ) : (
                <ROField
                  label={child.yearLevel === "TAFE" ? "Enrolment Doc" : "BYOD Doc"}
                  value="Not uploaded"
                />
              )}
            </div>
          </div>
        )}
      </DetailSection>
    );
  });
}
